import { Link } from "react-router-dom";
import {
  Container,
  SectionHeader,
  PullQuote,
  CTAStrip,
  ImageBlock,
} from "@/components/Sections";
import { IMAGES } from "@/lib/content";
import { ArrowUpRight, ArrowRight } from "lucide-react";

const CAREERS = [
  {
    title: "Carbon Auditors",
    blurb: "Supporting project compliance, reporting and verification.",
    to: "/pathways#carbon-auditor",
  },
  {
    title: "Nature Repair Auditors",
    blurb: "Assessing ecological outcomes and restoration performance.",
    to: "/pathways#nature-repair-auditor",
  },
  {
    title: "Biodiversity Assessors",
    blurb: "Monitoring species, habitat condition and environmental change.",
    to: "/pathways#biodiversity-assessor",
  },
  {
    title: "Natural Capital Monitors",
    blurb: "Supporting emerging environmental accounting systems.",
    to: "/pathways#natural-capital-monitor",
  },
  {
    title: "Restoration Coordinators",
    blurb: "Managing restoration projects and landscape recovery programs.",
    to: "/pathways/native-seed-collection",
  },
  {
    title: "Carbon Project Managers",
    blurb: "Leading Indigenous-led carbon and environmental projects.",
    to: "/pathways#carbon-project-manager",
  },
  {
    title: "Seed Collection Specialists",
    blurb: "Supporting restoration and regeneration through native seed systems.",
    to: "/pathways/native-seed-collection",
  },
  {
    title: "Environmental Data Officers",
    blurb: "Managing GIS, monitoring and environmental reporting systems.",
    to: "/pathways#natural-capital-monitor",
  },
];

const TRAINING_PROGRAMS = [
  {
    title: "Indigenous Native Seed Collection & Restoration",
    to: "/pathways/native-seed-collection",
  },
  { title: "Carbon Fundamentals", to: "/pathways#carbon-auditor" },
  { title: "Carbon Project Governance", to: "/pathways#carbon-project-manager" },
  { title: "Biodiversity Monitoring", to: "/pathways#biodiversity-assessor" },
  { title: "GIS & Environmental Data Collection", to: "/resources/gis-and-mapping" },
  { title: "Natural Capital & Nature Repair", to: "/pathways#natural-capital-monitor" },
  { title: "Environmental Reporting & Auditing", to: "/resources/environmental-reporting" },
  { title: "Indigenous Carbon Project Readiness", to: "/services" },
  { title: "Restoration Readiness Assessment", to: "/services" },
];

const PILLARS = [
  {
    number: "01",
    name: "Knowledge",
    body: "Traditional knowledge and cultural understanding.",
  },
  {
    number: "02",
    name: "Monitoring",
    body: "Measuring environmental change.",
  },
  {
    number: "03",
    name: "Restoration",
    body: "Improving landscape condition.",
  },
  {
    number: "04",
    name: "Stewardship",
    body: "Long-term care of Country.",
  },
  {
    number: "05",
    name: "Leadership",
    body: "Building local capability and decision-making authority.",
  },
];

const STRONG_COUNTRY = [
  "Skills",
  "Knowledge",
  "Employment",
  "Leadership",
  "Community capability",
];

const LEARNING_THROUGH_PRACTICE = [
  "Walking Country",
  "Monitoring landscapes",
  "Collecting field data",
  "Working with Elders",
  "Participating in restoration projects",
  "Learning from practitioners",
  "Applying knowledge in real-world settings",
];

export default function Leadership() {
  return (
    <div data-testid="page-leadership" className="bg-[#F6F1E6]">
      {/* HERO */}
      <section
        data-testid="leadership-hero"
        className="relative min-h-[95vh] flex items-end pt-32 pb-20"
      >
        <div className="absolute inset-0 warm-overlay">
          <img
            src={IMAGES.workforce}
            alt="Indigenous environmental leadership on Country"
            className="w-full h-full object-cover warm-tint"
          />
        </div>
        <Container className="relative z-10">
          <div className="max-w-5xl">
            <p
              className="overline mb-8 fade-up"
              style={{ color: "#E0B66B" }}
            >
              Indigenous Environmental Leadership
            </p>
            <h1
              data-testid="leadership-title"
              className="display-xxl text-[40px] sm:text-6xl md:text-7xl lg:text-[104px] mb-10 fade-up text-[#F6F1E6]"
              style={{ animationDelay: "0.1s" }}
            >
              Building the Indigenous
              <br />
              <span className="italic text-[#E0B66B]">Environmental Workforce</span>
              <br />
              of the Future.
            </h1>
            <p
              className="text-[#E5DFD0] text-base md:text-xl leading-relaxed max-w-2xl mb-8 fade-up"
              style={{ animationDelay: "0.2s" }}
            >
              Supporting Indigenous rangers, practitioners and communities to
              build careers in carbon, biodiversity, restoration and
              environmental management.
            </p>
            <p
              className="font-serif italic text-xl md:text-2xl text-[#F6F1E6] max-w-2xl leading-snug mb-12 fade-up"
              style={{ animationDelay: "0.3s" }}
            >
              Country has the answers. Carbon on Country helps build the skills,
              pathways and opportunities needed to care for Country in a
              changing world.
            </p>
            <div
              className="flex flex-col sm:flex-row gap-5 fade-up"
              style={{ animationDelay: "0.4s" }}
            >
              <Link
                to="/pathways"
                data-testid="leadership-hero-pathways"
                className="btn-primary"
              >
                Explore training pathways
                <ArrowRight size={16} />
              </Link>
              <Link
                to="/contact"
                data-testid="leadership-hero-contact"
                className="btn-ghost"
              >
                Start a conversation
                <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>
        </Container>
      </section>

      {/* CORE PHILOSOPHY — Country Has the Answers */}
      <section
        data-testid="leadership-philosophy"
        className="py-32 md:py-44 border-b border-[#1F2A1B]/10"
      >
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-5 lg:sticky lg:top-32">
              <p className="overline mb-6">Core Philosophy</p>
              <h2 className="font-serif text-5xl md:text-6xl lg:text-[88px] leading-[0.95] text-[#1F2A1B] mb-10">
                Country has
                <br />
                <span className="italic text-[#3F5132]">the answers.</span>
              </h2>
            </div>
            <div className="lg:col-span-7 space-y-8 text-[#4F5A48] text-lg md:text-xl leading-relaxed">
              <p>
                For more than{" "}
                <span className="text-[#1F2A1B] font-semibold">
                  60,000 years
                </span>{" "}
                Indigenous peoples have observed, managed and adapted to
                environmental change.
              </p>
              <p>
                Carbon on Country recognises that environmental stewardship does
                not begin with technology.
              </p>
              <p className="font-serif italic text-3xl md:text-4xl text-[#1F2A1B] leading-snug border-l-2 border-[#C45A3C] pl-8">
                It begins with Country.
              </p>
              <p>
                By combining Indigenous knowledge with modern environmental
                tools, we can build a new generation of practitioners equipped
                to restore landscapes, monitor change and strengthen community
                outcomes.
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* WHY CARBON ON COUNTRY EXISTS */}
      <section
        data-testid="leadership-why-exists"
        className="py-32 md:py-44 border-b border-[#1F2A1B]/10 bg-[#EBE3D0]"
      >
        <Container>
          <SectionHeader
            label="Why Carbon on Country Exists"
            title="Australia is investing billions"
            italic="— who will do the work?"
          />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start mt-4">
            <div className="lg:col-span-5">
              <p className="overline mb-5">The opportunity</p>
              <ul className="space-y-0">
                {[
                  "Carbon projects",
                  "Biodiversity markets",
                  "Nature Repair",
                  "Natural capital",
                  "Restoration",
                  "Climate adaptation",
                ].map((t, i) => (
                  <li
                    key={t}
                    className="flex items-baseline gap-4 py-4 border-b border-[#1F2A1B]/10"
                  >
                    <span className="font-serif italic text-[#C45A3C] text-xs w-6 shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-[#1F2A1B] text-lg">{t}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-7 lg:pl-12 lg:border-l lg:border-[#1F2A1B]/10">
              <p className="overline mb-8">A critical question</p>
              <ul className="space-y-8">
                {[
                  "Who will do the work?",
                  "Who will monitor outcomes?",
                  "Who will assess biodiversity?",
                  "Who will verify carbon projects?",
                  "Who will manage environmental data?",
                  "Who will steward Country for the next generation?",
                ].map((q, i) => (
                  <li
                    key={q}
                    className="flex items-baseline gap-5 border-b border-[#1F2A1B]/10 pb-6"
                  >
                    <span className="section-number">0{i + 1}</span>
                    <p className="font-serif text-2xl md:text-3xl leading-[1.15] text-[#1F2A1B]">
                      {q}
                    </p>
                  </li>
                ))}
              </ul>
              <p className="font-serif italic text-2xl md:text-3xl text-[#3F5132] mt-12 leading-snug max-w-2xl">
                Carbon on Country exists to help answer these questions.
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* LEADERSHIP GAP */}
      <section
        data-testid="leadership-gap"
        className="py-32 md:py-44 border-b border-[#1F2A1B]/10"
      >
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-end">
            <div className="lg:col-span-7">
              <p className="overline mb-6">The Leadership Gap</p>
              <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[1.02] text-[#1F2A1B] mb-10">
                From participation
                <br />
                to{" "}
                <span className="italic text-[#3F5132]">leadership.</span>
              </h2>
              <div className="space-y-6 text-[#4F5A48] text-lg leading-relaxed max-w-2xl">
                <p>
                  For decades Indigenous people have been consulted about
                  environmental projects.
                </p>
                <p className="font-serif italic text-2xl md:text-3xl text-[#1F2A1B] leading-snug">
                  The next step is leadership.
                </p>
                <p>
                  Carbon on Country supports pathways into roles where
                  Indigenous practitioners are not simply participating in
                  projects but{" "}
                  <span className="text-[#1F2A1B] font-semibold">
                    leading, managing, assessing and monitoring them.
                  </span>
                </p>
              </div>
            </div>
            <div className="lg:col-span-5">
              <ImageBlock
                src={IMAGES.monitoring}
                alt="Indigenous rangers leading environmental monitoring"
                caption="Practitioners · Managers · Leaders"
                className="aspect-[3/4]"
                testid="leadership-gap-image"
              />
            </div>
          </div>
        </Container>
      </section>

      {/* WORKFORCE PATHWAYS — Emerging Careers grid */}
      <section
        data-testid="leadership-careers"
        className="py-32 md:py-44 border-b border-[#1F2A1B]/10 bg-[#EBE3D0]"
      >
        <Container>
          <SectionHeader
            label="Workforce Pathways"
            title="Emerging"
            italic="environmental careers."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#1F2A1B]/10 border border-[#1F2A1B]/10">
            {CAREERS.map((c, i) => (
              <Link
                to={c.to}
                key={c.title}
                data-testid={`leadership-career-${i}`}
                className="group bg-[#F6F1E6] p-8 md:p-10 min-h-[260px] flex flex-col justify-between hover:bg-[#ECE4D2] transition-colors duration-500"
              >
                <div>
                  <span className="section-number">
                    — Role {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-serif text-2xl md:text-[28px] text-[#1F2A1B] leading-tight mt-5 group-hover:text-[#3F5132] transition-colors">
                    {c.title}
                  </h3>
                </div>
                <div className="mt-8">
                  <p className="text-[#4F5A48] text-sm leading-relaxed">
                    {c.blurb}
                  </p>
                  <ArrowUpRight
                    size={18}
                    className="text-[#8A8775] group-hover:text-[#C45A3C] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all mt-5"
                  />
                </div>
              </Link>
            ))}
          </div>
        </Container>
      </section>

      {/* FUTURE GENERATIONS */}
      <section
        data-testid="leadership-future-generations"
        className="py-32 md:py-44 border-b border-[#1F2A1B]/10"
      >
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-5">
              <ImageBlock
                src={IMAGES.regrowth}
                alt="Future generations — regrowth on Country"
                caption="Intergenerational stewardship"
                className="aspect-[4/5]"
                testid="future-generations-image"
              />
            </div>
            <div className="lg:col-span-7">
              <p className="overline mb-6">Future Generations</p>
              <h2 className="font-serif text-4xl md:text-6xl leading-[1.02] text-[#1F2A1B] mb-10">
                Investing in
                <br />
                <span className="italic text-[#3F5132]">future generations.</span>
              </h2>
              <div className="space-y-6 text-[#4F5A48] text-lg leading-relaxed">
                <p>
                  The future environmental workforce will be built by today's
                  rangers, young leaders and community practitioners.
                </p>
                <p>
                  Carbon on Country supports mentoring, training and career
                  pathways that strengthen Indigenous leadership across the
                  environmental sector.
                </p>
                <p className="font-serif italic text-2xl md:text-3xl text-[#1F2A1B] leading-snug border-l-2 border-[#C45A3C] pl-6">
                  Because environmental stewardship is not a short-term project.
                  It is an intergenerational responsibility.
                </p>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* TRAINING PROGRAMS */}
      <section
        data-testid="leadership-training-programs"
        className="py-32 md:py-44 border-b border-[#1F2A1B]/10 bg-[#EBE3D0]"
      >
        <Container>
          <SectionHeader
            label="Training Programs"
            title="A curriculum"
            italic="for environmental leaders."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1F2A1B]/10 border border-[#1F2A1B]/10">
            {TRAINING_PROGRAMS.map((p, i) => (
              <Link
                key={p.title}
                to={p.to}
                data-testid={`training-program-${i}`}
                className="bg-[#F6F1E6] p-8 md:p-10 min-h-[180px] flex flex-col justify-between hover:bg-[#ECE4D2] transition-colors duration-500 group"
              >
                <span className="section-number">
                  — Program {String(i + 1).padStart(2, "0")}
                </span>
                <div className="flex items-end justify-between gap-4">
                  <h3 className="font-serif text-xl md:text-2xl text-[#1F2A1B] leading-tight group-hover:text-[#3F5132] transition-colors">
                    {p.title}
                  </h3>
                  <ArrowUpRight
                    size={18}
                    className="text-[#8A8775] group-hover:text-[#C45A3C] transition-colors shrink-0"
                  />
                </div>
              </Link>
            ))}
          </div>
        </Container>
      </section>

      {/* LEARNING ON COUNTRY — Through Practice */}
      <section
        data-testid="leadership-learning"
        className="py-32 md:py-44 border-b border-[#1F2A1B]/10"
      >
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-5">
              <p className="overline mb-6">Learning on Country</p>
              <h2 className="font-serif text-4xl md:text-6xl leading-[1.02] text-[#1F2A1B] mb-10">
                Learning
                <br />
                <span className="italic text-[#3F5132]">through practice.</span>
              </h2>
              <p className="text-[#4F5A48] text-lg leading-relaxed mb-10 max-w-md">
                Real capability is built through field-based delivery, ranger-led
                activity and intergenerational mentoring.
              </p>
              <p className="text-[#4F5A48] text-base leading-relaxed max-w-md">
                Carbon on Country prioritises practical learning and on-Country
                delivery wherever possible.
              </p>
            </div>
            <div className="lg:col-span-7">
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-0">
                {LEARNING_THROUGH_PRACTICE.map((l, i) => (
                  <li
                    key={l}
                    className="flex items-baseline gap-4 py-5 border-b border-[#1F2A1B]/10"
                  >
                    <span className="font-serif italic text-[#C45A3C] text-sm w-8 shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-[#1F2A1B] text-base md:text-lg">
                      {l}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Container>
      </section>

      {/* ENVIRONMENTAL STEWARDSHIP FRAMEWORK — Signature 5-pillar model */}
      <section
        data-testid="leadership-framework"
        className="relative py-32 md:py-44 border-b border-[#1F2A1B]/10 bg-[#1F2A1B] text-[#F6F1E6] overflow-hidden"
      >
        <div className="absolute inset-0 opacity-25 pointer-events-none">
          <img
            src={IMAGES.opportunity}
            alt=""
            className="w-full h-full object-cover warm-tint"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1F2A1B] via-[#1F2A1B]/70 to-[#1F2A1B]" />
        </div>
        <Container className="relative z-10">
          <div className="mb-16 md:mb-24 max-w-4xl">
            <p
              className="overline mb-6"
              style={{ color: "#E0B66B" }}
            >
              The Environmental Stewardship Framework
            </p>
            <h2 className="font-serif text-4xl md:text-6xl lg:text-[80px] leading-[1.02] text-[#F6F1E6] mb-8">
              Our signature
              <br />
              <span className="italic" style={{ color: "#E0B66B" }}>
                model for capability.
              </span>
            </h2>
            <p className="text-[#E5DFD0] text-lg md:text-xl leading-relaxed max-w-2xl">
              Five pillars that move from knowledge through to leadership — the
              foundation of every Carbon on Country engagement.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-[#F6F1E6]/15 border border-[#F6F1E6]/15">
            {PILLARS.map((p, i) => (
              <div
                key={p.name}
                data-testid={`pillar-${p.number}`}
                className="bg-[#1F2A1B] p-8 md:p-10 min-h-[280px] flex flex-col justify-between"
              >
                <div>
                  <span
                    className="font-serif italic text-base"
                    style={{ color: "#E0B66B" }}
                  >
                    Pillar {p.number}
                  </span>
                  <h3 className="font-serif text-3xl md:text-4xl text-[#F6F1E6] mt-5 leading-tight">
                    {p.name}
                  </h3>
                </div>
                <p className="text-[#E5DFD0] text-sm md:text-base leading-relaxed">
                  {p.body}
                </p>
                <div
                  className="h-px mt-6"
                  style={{
                    background: i < PILLARS.length - 1
                      ? "linear-gradient(90deg, rgba(224,182,107,0.6), rgba(196,90,60,0.6))"
                      : "rgba(224,182,107,0.6)",
                  }}
                />
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* WHY IT MATTERS — Strong Country Requires Strong People */}
      <section
        data-testid="leadership-why-it-matters"
        className="py-32 md:py-44 border-b border-[#1F2A1B]/10"
      >
        <Container>
          <div className="max-w-4xl">
            <p className="overline mb-6">Why It Matters</p>
            <h2 className="font-serif text-4xl md:text-6xl lg:text-[80px] leading-[1.02] text-[#1F2A1B] mb-10">
              Strong Country
              <br />
              requires{" "}
              <span className="italic text-[#3F5132]">strong people.</span>
            </h2>
            <p className="text-[#4F5A48] text-lg md:text-xl leading-relaxed mb-10 max-w-3xl">
              Environmental investment should do more than generate reports,
              credits and projects. It should leave behind:
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-[#1F2A1B]/10 border border-[#1F2A1B]/10 mb-16">
              {STRONG_COUNTRY.map((s, i) => (
                <li
                  key={s}
                  data-testid={`legacy-${i}`}
                  className="bg-[#F6F1E6] p-8 min-h-[160px] flex flex-col justify-between"
                >
                  <span className="section-number">
                    — 0{i + 1}
                  </span>
                  <span className="font-serif text-2xl md:text-3xl text-[#1F2A1B] leading-snug">
                    {s}
                  </span>
                </li>
              ))}
            </ul>

            <PullQuote testid="leadership-northstar">
              The greatest environmental asset Australia possesses is not
              carbon. It is people who care for Country.
            </PullQuote>
          </div>
        </Container>
      </section>

      {/* FINAL CTA */}
      <section
        data-testid="leadership-final-cta"
        className="py-32 md:py-40 bg-[#EBE3D0]"
      >
        <Container>
          <div className="max-w-4xl">
            <p className="overline mb-6">Join us</p>
            <h2 className="font-serif text-4xl md:text-6xl lg:text-[80px] leading-[1.02] text-[#1F2A1B] mb-10">
              Join the next generation
              <br />
              of{" "}
              <span className="italic text-[#3F5132]">
                environmental leaders.
              </span>
            </h2>
            <p className="text-[#4F5A48] text-lg md:text-xl leading-relaxed mb-8 max-w-3xl">
              Whether you are an Indigenous ranger, Traditional Owner
              organisation, community group or environmental practitioner,
              Carbon on Country can help build the skills and pathways needed to
              steward Country for future generations.
            </p>
            <p className="font-serif italic text-2xl md:text-3xl text-[#1F2A1B] leading-snug mb-12 max-w-3xl border-l-2 border-[#C45A3C] pl-6">
              Country has the answers. Together we can build the capability to
              listen.
            </p>
            <div className="flex flex-wrap gap-5">
              <Link
                to="/contact"
                data-testid="leadership-final-cta-contact"
                className="btn-primary"
              >
                Start a conversation
                <ArrowRight size={16} />
              </Link>
              <Link
                to="/pathways"
                data-testid="leadership-final-cta-pathways"
                className="btn-ghost"
              >
                Explore training pathways
                <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}
